/**
 * useMathPlugins — returns remark/rehype plugin tuples for TeX math, or null
 * (CEL-2102).
 *
 * Flow per render of CellCogMarkdown:
 *   1. detectMath(content) (cheap; null for the vast majority of messages).
 *   2. If math is present and the lazy chunk is not loaded yet, load it once
 *      (module-scoped cache shared by every renderer on the page). Until it
 *      arrives the content renders exactly as before, with the dollar text
 *      literal, then re-renders with KaTeX.
 *   3. Return { remark: [remarkMath, opts], rehype: [rehypeKatex, opts] }.
 *
 * singleDollarTextMath follows detectMath's verdict so prices never become
 * math. A failed chunk load is swallowed: the message simply keeps its raw
 * TeX text, which is today's behavior.
 */
import { useEffect, useMemo, useState } from 'react';
import detectMath from './plugins/mathDetect';

let loadedMath = null;
let loadingMath = null;

export function loadMathPlugins() {
  if (loadedMath) return Promise.resolve(loadedMath);
  if (!loadingMath) {
    loadingMath = import(/* webpackChunkName: "cellcog-markdown-math" */ './plugins/mathPlugins')
      .then((mod) => {
        loadedMath = mod;
        return mod;
      })
      .catch((err) => {
        loadingMath = null; // allow a retry on the next math-bearing render
        throw err;
      });
  }
  return loadingMath;
}

// throwOnError:false renders a bad formula in KaTeX's error color instead of
// throwing; strict:'ignore' silences console noise for non-strict TeX
// (unicode in math etc.) that agents emit routinely.
const KATEX_OPTIONS = { throwOnError: false, strict: 'ignore' };

export default function useMathPlugins(content) {
  const detection = useMemo(() => detectMath(content), [content]);
  const [mod, setMod] = useState(loadedMath);

  useEffect(() => {
    if (!detection || mod) return undefined;
    let alive = true;
    loadMathPlugins()
      .then((m) => {
        if (alive) setMod(m);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [detection, mod]);

  return useMemo(() => {
    if (!detection || !mod) return null;
    return {
      remark: [mod.remarkMath, { singleDollarTextMath: detection.singleDollar }],
      rehype: [mod.rehypeKatex, KATEX_OPTIONS],
    };
  }, [detection, mod]);
}
